/**
 * @fileoverview Static per-component help content keyed by band kind (Phase 5, Workstream L3).
 * @module ui-admin/help/componentHelpContent
 *
 * Responsibilities: - Provide a `ComponentHelpEntry` for every `BandKind` shown in the
 *                     `ComponentHelpPopover` (what it is, how it renders, configuration tips,
 *                     and which help-panel section to deep-link to).
 * Dependencies:     - `BandKind` from `@firesquirrel/document-render`; `ComponentHelpEntry`/
 *                     `HelpSectionId` from `./types`.
 * Architecture:     - Pure static data bundled with the app. No API/Dexie reads, no mock data.
 *                     Local-first by construction.
 * Notes:            - The `satisfies Record<BandKind, ComponentHelpEntry>` assertion at the
 *                     bottom is a compile-time exhaustiveness check: adding a new BandKind
 *                     without a help entry (or vice-versa) is a TypeScript error.
 */

import type { BandKind } from '@firesquirrel/document-render';
import type { ComponentHelpEntry } from './types';

/**
 * @contract COMPONENT_HELP
 * Purpose: Lookup of just-in-time help content for each band kind.
 * Rules: Must contain exactly one entry per `BandKind` value (enforced by `satisfies` below).
 *        Every entry has a non-empty `whatItIs`, `rendersAs`, and at least one tip.
 */
export const COMPONENT_HELP = {
  letterhead: {
    whatItIs: 'The top-of-page header band carrying your department logo, name, and branding.',
    rendersAs: 'A banner at the top of the first page (and optionally every page) with your logo and department details.',
    tips: [
      'Pull the logo and department details from your department theme so it stays consistent.',
      'Keep it compact — a tall letterhead pushes the body content down on every page.',
    ],
    helpSection: 'branding',
  },
  coverPage: {
    whatItIs: 'A full leading cover page rendered before the document body.',
    rendersAs: 'A complete page on its own, ahead of the first body page.',
    tips: [
      'Use it for a formal cover letter or a title page.',
      'The cover page has full parity with the body — every band type is available on it.',
    ],
    helpSection: 'coverPage',
  },
  recipientBlock: {
    whatItIs: 'A formatted address / property block, positioned like the address on a real letter.',
    rendersAs: 'A left-aligned block of recipient name and address lines near the top of the body.',
    tips: [
      'Use merge fields like {{ownerName}} and {{propertyAddress}} so it fills in per case.',
      'Place it below the letterhead and above the subject line for a standard letter layout.',
    ],
    helpSection: 'mergeFields',
  },
  subjectLine: {
    whatItIs: 'A bold subject line, e.g. "Re: 123 Main St \u2014 Notice of Violation."',
    rendersAs: 'A single emphasised line introducing the purpose of the document.',
    tips: [
      'Combine static text with a merge field, e.g. "Re: {{propertyAddress}}".',
      'Keep it to one line so it reads as a heading.',
    ],
    helpSection: 'mergeFields',
  },
  salutation: {
    whatItIs: 'An opening greeting line, e.g. "Dear {{ownerName}},".',
    rendersAs: 'A short greeting line at the start of the letter body.',
    tips: [
      'Use a merge field for the name so it personalises automatically.',
      'Provide a sensible fallback in your wording in case the name is missing.',
    ],
    helpSection: 'mergeFields',
  },
  signatureBlock: {
    whatItIs: 'Signature line(s) with inspector name, title, and date.',
    rendersAs: 'A closing block with a signature space, name, title, and date near the end of the body.',
    tips: [
      'Use merge fields for the inspector name, title, and date so it fills in per document.',
      'Leave vertical space above the name for a physical or image signature.',
    ],
    helpSection: 'mergeFields',
  },
  legal: {
    whatItIs: 'A legal disclaimer or footer text band, typically set in smaller type.',
    rendersAs: 'A block of fine-print legal text, usually toward the bottom of the document.',
    tips: [
      'Use a smaller font size to set it apart from the main body.',
      'Keep the wording fixed — review legal text with your department before publishing.',
    ],
    helpSection: 'branding',
  },
  footer: {
    whatItIs: 'Page numbers and repeating footer text across all pages.',
    rendersAs: 'A repeating strip at the bottom of every page (e.g. page number, department name).',
    tips: [
      'Footers repeat on every page — keep them short.',
      'Include a page-number token if your document may run to multiple pages.',
    ],
    helpSection: 'branding',
  },
  richText: {
    whatItIs: 'Free-form formatted text \u2014 the body of a letter or a legal narrative.',
    rendersAs: 'Formatted paragraphs exactly as typed, with bold/italic/lists preserved.',
    tips: [
      'Type {{ to insert a merge field anywhere in the text.',
      'Use headings and lists to keep long narratives readable.',
    ],
    helpSection: 'mergeFields',
  },
  image: {
    whatItIs: 'A static image or logo placed at a fixed position in the document.',
    rendersAs: 'An inline image at its configured size and alignment.',
    tips: [
      'Use a high-resolution source so it stays crisp in the PDF.',
      'For the department logo, prefer the letterhead band so branding stays centralised.',
    ],
    helpSection: 'branding',
  },
  divider: {
    whatItIs: 'A horizontal rule used to separate sections visually.',
    rendersAs: 'A thin horizontal line spanning the content width.',
    tips: [
      'Use sparingly — whitespace often separates content more cleanly than a line.',
    ],
    helpSection: 'overview',
  },
  spacer: {
    whatItIs: 'Vertical whitespace between bands.',
    rendersAs: 'An empty gap of the configured height between the surrounding bands.',
    tips: [
      'Use it to control rhythm instead of adding blank lines inside a text band.',
    ],
    helpSection: 'overview',
  },
  pageBreak: {
    whatItIs: 'Forces the next band onto a new page.',
    rendersAs: 'A hard page break — everything after it starts on a fresh page.',
    tips: [
      'Use it to keep a section (like an appendix) on its own page.',
      'Check the preview — page breaks shift where later content lands.',
    ],
    helpSection: 'previewFidelity',
  },
  callout: {
    whatItIs: 'A bordered or shaded callout box for notices, warnings, or highlights.',
    rendersAs: 'A boxed or shaded block that stands out from the surrounding body text.',
    tips: [
      'Use it for the single most important instruction or deadline.',
      'Keep callout text short so it stays scannable.',
    ],
    helpSection: 'overview',
  },
  violationsList: {
    whatItIs: 'Dynamic. Repeats once per violation \u2014 expands to the full list at render time.',
    rendersAs: 'A repeating list or table, one entry per violation on the case.',
    tips: [
      'Use "Preview with sample data" to see how the list expands.',
      'Configure the columns/fields once — every violation uses the same layout.',
    ],
    helpSection: 'dynamicComponents',
  },
  photoGallery: {
    whatItIs: 'Dynamic. Repeats per inspection photo \u2014 inline, grid, or appendix layout.',
    rendersAs: 'A grid or sequence of inspection photos, one tile per photo.',
    tips: [
      'Choose an appendix layout for many photos so they don\u2019t break up the narrative.',
      'Preview with sample data to check the grid density.',
    ],
    helpSection: 'dynamicComponents',
  },
  metadataGrid: {
    whatItIs: 'Dynamic. A key/value grid of case and entity metadata fields.',
    rendersAs: 'A two-column grid of labelled case/entity values.',
    tips: [
      'Pick only the fields the reader needs — a long grid is hard to scan.',
      'Field values resolve from the case at render time.',
    ],
    helpSection: 'dynamicComponents',
  },
  formResponses: {
    whatItIs: 'Dynamic. Repeats per answered inspection form field.',
    rendersAs: 'A list of question/answer rows from the completed inspection form.',
    tips: [
      'Use it to embed the inspection checklist results directly in the document.',
      'Preview with sample data to confirm long answers wrap cleanly.',
    ],
    helpSection: 'dynamicComponents',
  },
  advancedFields: {
    whatItIs: 'Dynamic. Resolved values from a linked inspection template\u2019s advanced fields.',
    rendersAs: 'Values pulled from the linked inspection template, laid out as configured.',
    tips: [
      'Make sure the document is linked to the right inspection template.',
      'Preview with sample data to verify the advanced fields resolve as expected.',
    ],
    helpSection: 'dynamicComponents',
  },
} satisfies Record<BandKind, ComponentHelpEntry>;

/** Convenience alias for the keys of {@link COMPONENT_HELP}. */
export type ComponentHelpKind = keyof typeof COMPONENT_HELP;
